import React, { useEffect, useState } from 'react';
import ParticleNetworkAnimation from './ParticleNetworkAnimation';
import LottieHeroIllustration from './LottieHeroIllustration';
import { useTheme } from '../contexts/ThemeContext';

interface HeroProps {
  onNeuralNetworkCode?: string;
}

const Hero: React.FC<HeroProps> = ({ onNeuralNetworkCode }) => {
  const { theme } = useTheme();
  
  const fullText = "Modern Digital Solutions";
  const [typedText, setTypedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const typingSpeed = 100;
  const deletingSpeed = 50;
  const pauseDuration = 2000;

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isDeleting && currentIndex < fullText.length) {
        setTypedText(fullText.substring(0, currentIndex + 1));
        setCurrentIndex(currentIndex + 1);
      } else if (isDeleting && currentIndex > 0) {
        setTypedText(fullText.substring(0, currentIndex - 1));
        setCurrentIndex(currentIndex - 1);
      } else if (currentIndex === fullText.length) {
        setTimeout(() => setIsDeleting(true), pauseDuration);
      } else if (currentIndex === 0 && isDeleting) {
        setIsDeleting(false);
      }
    }, isDeleting ? deletingSpeed : typingSpeed);

    return () => clearTimeout(timer);
  }, [currentIndex, isDeleting, fullText, typingSpeed, deletingSpeed, pauseDuration]);

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16 sm:pt-20">
      {/* Neural Network Background Animation Container */}
      <div className="absolute inset-0 z-0">
        {/* Theme-aware overlay for readability - reduced opacity on mobile */}
        <div className="absolute inset-0 dark:bg-gray-900 dark:bg-opacity-30 light:bg-white light:bg-opacity-20 sm:dark:bg-opacity-50 sm:light:bg-opacity-30 z-10"></div>
        
        {/* Particle Network Animation Background */}
        <div className="absolute inset-0 dark:bg-gradient-to-br dark:from-navy dark:via-navy-light dark:to-navy-lighter light:bg-gradient-to-br light:from-light-bg light:via-light-bg-secondary light:to-slate-100">
          <ParticleNetworkAnimation />
        </div>
      </div>

      {/* Main Content Container */}
      <div className="relative z-20 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center text-center">
          
          {/* Animation Above */}
          <div className="mb-4 sm:mb-8 flex justify-center">
            <div className="w-full max-w-2xl relative">
              {/* Background for illustration - matches body color */}
              <div className="absolute inset-0 rounded-3xl dark:bg-slate-900 light:bg-gray-100"></div>
              <LottieHeroIllustration className="relative z-10" />
            </div>
          </div>

          {/* Text Content Below */}
          <div className="text-center hero-content">
            {/* Trust Badge */}
            <div className="mb-8 relative z-10">
              <div className="inline-flex items-center px-4 py-2 glass rounded-full text-sm font-medium">
                <span className="text-accent-light mr-2">✓</span>
                Trusted by 50+ businesses worldwide
              </div>
            </div>

            {/* Main Headline */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 stagger-1 pb-4 relative z-10">
              <span className="block dark:text-white light:text-gray-900 mb-2">Transform Your Business with</span>
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-light pb-4">
                {typedText}
                <span className="animate-pulse text-accent ">|</span>
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl dark:text-gray-300 light:text-gray-600 mb-8 leading-relaxed text-center px-4 relative z-10">
              We create cutting-edge software solutions that drive growth, enhance user experience, 
              and transform businesses across industries with innovative technology.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12 px-4 relative z-10">
              <button 
                className="group relative px-6 sm:px-8 py-3 sm:py-4 bg-accent hover:bg-accent-dark text-white font-semibold rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-accent/25 magnetic w-full sm:w-auto"
                onClick={() => {
                  const servicesSection = document.getElementById('services');
                  if (servicesSection) {
                    servicesSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                <span className="relative z-10 text-sm sm:text-base">Our Services</span>
                <div className="absolute inset-0 bg-gradient-to-r from-accent to-accent-light rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
              
              <button 
                className="group relative px-6 sm:px-8 py-3 sm:py-4 bg-accent hover:bg-accent-dark text-white font-semibold rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-accent/25 magnetic w-full sm:w-auto"
                onClick={() => {
                  const aboutSection = document.getElementById('about');
                  if (aboutSection) {
                    aboutSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                <span className="relative z-10 flex items-center justify-center text-sm sm:text-base">
                  Who Are We
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-accent to-accent-light rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
            </div>

            {/* Scroll Indicator */}
            <div className="relative z-10">
              <div className={`flex flex-col items-center ${theme === 'dark' ? 'text-gray-300'  : 'text-gray-600'} `}>
                <span className="text-sm mb-2 py-1">Scroll to explore</span>
                {/* Mouse Design */}
                <div className="relative">
                  {/* Mouse Body */}
                  <div className="w-10 h-14 border-2 dark:border-gray-300 border-gray-400 rounded-full flex justify-center relative bg-transparent">
                    {/* Scroll Wheel */}
                    <div className="w-1.5 h-4 border-2 dark:bg-accent border-gray-300 rounded-full mt-2 animate-bounce"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
